import { useGlobalProperty } from '@/hooks/useGlobalProperty';
import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { propertyAPI, facturacionAPI, API } from '@/services/api';
import { FileText, Upload, Save, ImageIcon, Percent, Hash } from 'lucide-react';
import { toast } from 'sonner';

const TAX_DEFS = [
  { code: 'iva', label: 'IVA', hint: 'Se suma sobre el subtotal' },
  { code: 'reteiva', label: 'ReteIVA', hint: 'Se resta y se calcula sobre el valor del IVA' },
  { code: 'reteica', label: 'ReteICA', hint: 'Se resta y se calcula sobre el subtotal' },
  { code: 'retefuente', label: 'Retefuente', hint: 'Se resta y se calcula sobre el subtotal' },
];

const emptyConfig = {
  razon_social: '', nit: '', direccion_fiscal: '', telefono: '', email_facturacion: '',
  resolucion_dian: '', rango_desde: '', rango_hasta: '', prefijo: 'FE', correlativo_actual: 0,
  taxes: {
    iva: { enabled: true, pct: 19 },
    reteiva: { enabled: false, pct: 15 },
    reteica: { enabled: false, pct: 0.966 },
    retefuente: { enabled: false, pct: 2.5 },
  }
};

export default function FacturacionConfigPage() {
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId] = useGlobalProperty(properties);
  const [config, setConfig] = useState(emptyConfig);
  const [hasLogo, setHasLogo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoVersion, setLogoVersion] = useState(0);
  const fileRef = useRef(null);

  useEffect(() => {
    propertyAPI.getAll().then(r => setProperties(r.data)).catch(() => toast.error('Error al cargar propiedades'));
  }, []);

  useEffect(() => {
    if (!selectedPropertyId) return;
    setLoading(true);
    facturacionAPI.getConfig(selectedPropertyId)
      .then(res => {
        const d = res.data;
        setConfig({ ...emptyConfig, ...d, taxes: { ...emptyConfig.taxes, ...(d.taxes || {}) } });
        setHasLogo(!!d.logo_path);
        setLogoVersion(v => v + 1);
      })
      .catch(() => toast.error('Error al cargar configuración de facturación'))
      .finally(() => setLoading(false));
  }, [selectedPropertyId]);

  const setField = (k, v) => setConfig(c => ({ ...c, [k]: v }));
  const setTax = (code, key, value) => setConfig(c => ({
    ...c,
    taxes: { ...c.taxes, [code]: { ...c.taxes[code], [key]: value } }
  }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await facturacionAPI.saveConfig(selectedPropertyId, {
        ...config,
        correlativo_actual: parseInt(config.correlativo_actual) || 0,
      });
      toast.success('Configuración de facturación guardada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedPropertyId) return;
    setUploading(true);
    try {
      await facturacionAPI.uploadLogo(selectedPropertyId, file);
      setHasLogo(true);
      setLogoVersion(v => v + 1);
      toast.success('Logo cargado correctamente');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir el logo');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (loading && !config.razon_social) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="facturacion-config-page">
        <div>
          <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope'}} data-testid="fact-config-title">
            Configuración de Facturación
          </h1>
          <p className="text-muted-foreground mt-1">
            Datos fiscales, membrete, numeración e impuestos de la propiedad seleccionada
          </p>
        </div>

        {/* Datos fiscales + Logo */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Datos fiscales y membrete
            </CardTitle>
            <CardDescription>Esta información aparece en el encabezado de remisiones y facturas</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Logo */}
            <div className="flex items-center gap-5 p-4 border rounded-lg bg-slate-50">
              <div className="w-24 h-24 border rounded-lg bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
                {hasLogo ? (
                  <img
                    src={`${API}/facturacion/${selectedPropertyId}/logo?v=${logoVersion}`}
                    alt="Logo de la propiedad"
                    className="max-w-full max-h-full object-contain"
                    data-testid="fact-logo-preview"
                  />
                ) : (
                  <ImageIcon className="h-8 w-8 text-slate-300" data-testid="fact-logo-placeholder" />
                )}
              </div>
              <div className="space-y-2">
                <input type="file" accept="image/jpeg,image/png,image/webp" ref={fileRef} className="hidden" onChange={handleLogoUpload} data-testid="fact-logo-input" />
                <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="fact-logo-upload-btn">
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading ? 'Subiendo...' : 'Cargar imagen del logo'}
                </Button>
                <p className="text-xs text-slate-500">JPG, PNG o WEBP. Máx 5MB. Se usa en el membrete de remisiones y facturas.</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Razón Social</Label>
                <Input value={config.razon_social} onChange={(e) => setField('razon_social', e.target.value)} placeholder="Hotel Edén S.A.S." data-testid="fact-razon-social" />
              </div>
              <div className="space-y-2">
                <Label>NIT</Label>
                <Input value={config.nit} onChange={(e) => setField('nit', e.target.value)} placeholder="900123456-7" data-testid="fact-nit" />
              </div>
              <div className="space-y-2">
                <Label>Dirección fiscal</Label>
                <Input value={config.direccion_fiscal} onChange={(e) => setField('direccion_fiscal', e.target.value)} placeholder="Carrera 99 #66-43E" data-testid="fact-direccion" />
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input value={config.telefono} onChange={(e) => setField('telefono', e.target.value)} placeholder="3001234567" data-testid="fact-telefono" />
              </div>
              <div className="space-y-2">
                <Label>Email de facturación</Label>
                <Input type="email" value={config.email_facturacion} onChange={(e) => setField('email_facturacion', e.target.value)} placeholder="facturacion@hotel.com" data-testid="fact-email" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Numeración DIAN */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Hash className="h-5 w-5" />
              Resolución DIAN y numeración
            </CardTitle>
            <CardDescription>Un solo correlativo por propiedad, compartido entre remisiones y facturas</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Resolución DIAN Nº</Label>
                <Input value={config.resolucion_dian} onChange={(e) => setField('resolucion_dian', e.target.value)} placeholder="18764000012345" data-testid="fact-resolucion" />
              </div>
              <div className="space-y-2">
                <Label>Rango desde</Label>
                <Input value={config.rango_desde} onChange={(e) => setField('rango_desde', e.target.value)} placeholder="FE-0001" data-testid="fact-rango-desde" />
              </div>
              <div className="space-y-2">
                <Label>Rango hasta</Label>
                <Input value={config.rango_hasta} onChange={(e) => setField('rango_hasta', e.target.value)} placeholder="FE-5000" data-testid="fact-rango-hasta" />
              </div>
              <div className="space-y-2">
                <Label>Prefijo</Label>
                <Input value={config.prefijo} onChange={(e) => setField('prefijo', e.target.value)} placeholder="FE" className="w-28" data-testid="fact-prefijo" />
              </div>
              <div className="space-y-2">
                <Label>Correlativo actual</Label>
                <Input type="number" min="0" value={config.correlativo_actual} onChange={(e) => setField('correlativo_actual', e.target.value)} className="w-36" data-testid="fact-correlativo" />
                <p className="text-xs text-slate-500">El próximo documento será {(config.prefijo || 'FE')}-{String((parseInt(config.correlativo_actual) || 0) + 1).padStart(4, '0')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Impuestos */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Percent className="h-5 w-5" />
              Impuestos y retenciones
            </CardTitle>
            <CardDescription>Valores por defecto; en cada documento se pueden activar o desactivar manualmente</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {TAX_DEFS.map(t => (
              <div key={t.code} className="flex items-center gap-4 p-3 border rounded-lg" data-testid={`fact-tax-${t.code}`}>
                <label className="flex items-center gap-2 cursor-pointer min-w-[140px]">
                  <Checkbox
                    checked={!!config.taxes[t.code]?.enabled}
                    onCheckedChange={(v) => setTax(t.code, 'enabled', !!v)}
                    data-testid={`fact-tax-${t.code}-enabled`}
                  />
                  <span className="font-medium">{t.label}</span>
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number" step="0.001" min="0"
                    value={config.taxes[t.code]?.pct ?? 0}
                    onChange={(e) => setTax(t.code, 'pct', parseFloat(e.target.value) || 0)}
                    className="w-28"
                    disabled={!config.taxes[t.code]?.enabled}
                    data-testid={`fact-tax-${t.code}-pct`}
                  />
                  <span className="text-sm text-slate-500">%</span>
                </div>
                <span className="text-xs text-slate-400 hidden md:block">{t.hint}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="bg-slate-800 hover:bg-slate-700" data-testid="fact-config-save-btn">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Guardando...' : 'Guardar configuración'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
